import { memo } from 'react';
import { Box, Card, Divider, Table, Tag, Text } from '@zillow/constellation-10';
import type { YourRentalPaymentTransactionsDto } from '@zg-rentals/ts-your-rental-api-client';
import { CtaButton } from '../shared';
import { TAG_APPEARANCE_MAP } from '../../constants';
import TransactionsTableHeader from './TransactionsTableHeader';

const CONTENT = {
  TYPE_HEADER: 'Type',
  STATUS_HEADER: 'Status',
  AMOUNT_HEADER: 'Amount',
} as const;

const TransactionsTable = memo(
  ({
    transactionSection,
    listingAlias,
    isDesktop = false,
  }: {
    transactionSection: YourRentalPaymentTransactionsDto;
    listingAlias?: string;
    isDesktop?: boolean;
  }) => {
    const { transactionHeaderText, transactions, cta } = transactionSection;

    if (isDesktop) {
      return (
        <Card
          elevated={false}
          css={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            padding: '{spacing.default} {spacing.loose} {spacing.loose}',
          }}
          data-testid="RentReporting-TransactionsTableDesktop"
        >
          <TransactionsTableHeader headerText={transactionHeaderText} isDesktop={isDesktop} />
          <Box
            css={{
              maxHeight: '30vh',
              overflowY: 'auto',
              width: '100%',
            }}
          >
            <Table.Root
              alignments={['start', 'start', 'start', 'end']}
              appearance="horizontal"
              size="md"
              aria-label="Payments transactions table"
            >
              <Table.Header sticky>
                <Table.Row>
                  <Table.HeaderCell>{CONTENT.TYPE_HEADER}</Table.HeaderCell>
                  <Table.HeaderCell>{CONTENT.STATUS_HEADER}</Table.HeaderCell>
                  <Table.HeaderCell>{CONTENT.AMOUNT_HEADER}</Table.HeaderCell>
                  <Table.HeaderCell>&nbsp;</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {transactions.map((transaction, index) => (
                  <Table.Row key={transaction.typeText} data-testid={`RentReporting-TransactionRow-${index}`}>
                    <Table.Cell>
                      <Text data-testid={`RentReporting-TransactionType-${index}`}>{transaction.typeText}</Text>
                    </Table.Cell>
                    <Table.Cell>
                      <Tag
                        tone={TAG_APPEARANCE_MAP[transaction.statusTag.tagAppearance]}
                        size="md"
                        data-testid={`RentReporting-TransactionStatus-${index}`}
                      >
                        {transaction.statusTag.tagText}
                      </Tag>
                    </Table.Cell>
                    <Table.Cell>
                      <Text textStyle="body-lg-bold" data-testid={`RentReporting-TransactionAmount-${index}`}>
                        {transaction.amount}
                      </Text>
                    </Table.Cell>
                    <Table.Cell>
                      {transaction.cta && (
                        <CtaButton
                          cta={transaction.cta}
                          listingAlias={listingAlias}
                          dataTestId={`RentReporting-TransactionCta-${index}`}
                        />
                      )}
                      {!transaction.cta && <Text data-testid={`RentReporting-TransactionCta-${index}`}>-</Text>}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>
          </Box>
          {cta && (
            <Box css={{ width: '100%', display: 'flex', justifyContent: 'flex-start', marginTop: 'default' }}>
              <CtaButton cta={cta} listingAlias={listingAlias} dataTestId="TransactionsTable-BottomCta" />
            </Box>
          )}
        </Card>
      );
    }

    // Mobile view
    return (
      <Box
        css={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 'default',
        }}
        data-testid="RentReporting-TransactionsTableMobile"
      >
        <Box css={{ display: 'flex', flexDirection: 'column', gap: 'tight' }}>
          <TransactionsTableHeader headerText={transactionHeaderText} isDesktop={isDesktop} />
          <Box
            css={{
              maxHeight: '30vh',
              overflowY: 'auto',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {transactions.map((transaction, index) => (
              <Box
                key={transaction.typeText}
                css={{ display: 'flex', flexDirection: 'column' }}
                data-testid={`RentReporting-TransactionRow-${index}`}
              >
                <Box
                  css={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    paddingBlock: 'tight',
                  }}
                >
                  <Box css={{ display: 'flex', flexDirection: 'column', gap: 'tighter', alignItems: 'flex-start' }}>
                    <Tag
                      tone={TAG_APPEARANCE_MAP[transaction.statusTag.tagAppearance]}
                      size="sm"
                      data-testid={`RentReporting-TransactionStatus-${index}`}
                    >
                      {transaction.statusTag.tagText}
                    </Tag>
                    <Text data-testid={`RentReporting-TransactionType-${index}`}>{transaction.typeText}</Text>
                    <Text textStyle="body-lg-bold" data-testid={`RentReporting-TransactionAmount-${index}`}>
                      {transaction.amount}
                    </Text>
                  </Box>
                  {transaction.cta && (
                    <CtaButton
                      cta={transaction.cta}
                      listingAlias={listingAlias}
                      dataTestId={`RentReporting-TransactionCta-${index}`}
                    />
                  )}
                </Box>
                {index < transactions.length - 1 && <Divider />}
              </Box>
            ))}
          </Box>
        </Box>
        {cta && (
          <Box css={{ width: '100%', display: 'flex', justifyContent: 'flex-start' }}>
            <CtaButton cta={cta} listingAlias={listingAlias} dataTestId="TransactionsTable-BottomCta" />
          </Box>
        )}
      </Box>
    );
  },
);

TransactionsTable.displayName = 'TransactionsTable';

export default TransactionsTable;
